import React, { useEffect, useState } from 'react';
import {
  advanceSearchOperation,
  AdvanceSearchUserDetails,
} from './operations/advance-search-operation';
import {
  Gender,
  UserSummaryType,
} from '../../../api/types/user/user-summary.type';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTablePageEvent,
  DataTableRowClickEvent,
} from 'primereact/datatable';
import { ColumnFilter } from '../../../component/filters/column-filter';
import { UserAvatarSmall } from '../../../component/user-avatar/user-avatar-small';
import { UserType } from '../../../api/types/user/user-type';
import generateAndDownloadExcelFile from '../../../component/excel/generate-and-download-excel-file';
import { Button } from 'primereact/button';
import { PhoneNumberComponent } from '../../../utils/international-to-israeli';
import globalConfigStore from '../../../stores/global-config-store';
import { observer } from 'mobx-react-lite';
import {
  OutlookMailDialog,
  OutlookMailDialogUser,
} from '../../../component/outlook-mail-dialog/outlook-mail-dialog';

interface AdvanceSearchDatamodel {
  setSelectedUser: (user: UserSummaryType | undefined) => any;
  userRole: UserType | undefined;
  newUserToAdd?: UserSummaryType;
}

export const AdvanceSearch: React.FC<AdvanceSearchDatamodel> = observer(
  ({ setSelectedUser, userRole, newUserToAdd }) => {
    const [users, setUsers] = useState<UserSummaryType[]>([]);
    const [firstName, setFirstName] = useState('');
    const [userDetails, setUserDetails] = useState<
      AdvanceSearchUserDetails | undefined
    >(undefined);
    const [majorsIds, setMajorsIds] = useState<number[]>([]);
    const [abstractCoursesIds, setAbstractCoursesIds] = useState<number[]>([]);
    const [abstractProgramIds, setAbstractProgramIds] = useState<number[]>([]);
    const [programStartYear, setProgramStartYear] = useState<
      number | undefined
    >();
    const [branchIds, setBranchIds] = useState<number[]>([]);

    // Pagination state
    const [offset, setOffset] = useState(0);
    const [limit, setLimit] = useState(10);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    // Add selection state for DataTable
    const [selectedUsers, setSelectedUsers] = useState<UserSummaryType[]>([]);

    // Outlook dialog state (per-row "send mail" action)
    const [showOutlookDialog, setShowOutlookDialog] = useState(false);
    const [outlookUsers, setOutlookUsers] = useState<OutlookMailDialogUser[]>(
      [],
    );
    const [outlookTitle, setOutlookTitle] = useState<string>('ייצוא לאאוטלוק');

    const openOutlookForUser = (user: UserSummaryType) => {
      setOutlookUsers([
        {
          userId: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          nationalId: user.nationalId || '',
          mail: user.email,
        },
      ]);
      setOutlookTitle(
        `שליחת מייל ל-${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
      );
      setShowOutlookDialog(true);
    };

    // Add new user to the top of the list when newUserToAdd changes
    useEffect(() => {
      if (newUserToAdd) {
        setUsers((prevUsers) => [newUserToAdd, ...prevUsers]);
        setTotal((prevTotal) => prevTotal + 1);
      }
    }, [newUserToAdd]);

    // Fetch users with current pagination parameters
    const fetchUsers = async (newOffset: number, newLimit: number) => {
      await advanceSearchOperation(
        userRole,
        newOffset,
        newLimit,
        setUsers,
        setTotal,
        setLoading,
        firstName,
        userDetails,
        majorsIds,
        abstractCoursesIds,
        abstractProgramIds,
        programStartYear,
        branchIds,
      );
    };

    // Fetch users when component mounts or userRole changes
    useEffect(() => {
      const loadInitialUsers = async () => {
        if (globalConfigStore.isReady) {
          const initialLimit = 10;
          await fetchUsers(0, initialLimit);
          setOffset(0);
          setLimit(initialLimit);
        }
      };
      loadInitialUsers();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userRole, globalConfigStore.isReady]);

    // Handle page change from paginator
    const onPageChange = (event: DataTablePageEvent) => {
      console.log('Page change event:', event);
      const newOffset = event.first;
      const newLimit = event.rows;
      console.log('New offset:', newOffset, 'New limit:', newLimit);
      setOffset(newOffset);
      setLimit(newLimit);
      fetchUsers(newOffset, newLimit);
    };

    const updateSearchParameters = async (
      userDetails?: AdvanceSearchUserDetails,
      majorsIds?: number[],
      abstractCousresIds?: number[],
      abstractProgramIds?: number[],
      supervisoryInstitutionIds?: number[],
      customFilterValues?: Record<string, any>,
      programStartYear?: number,
      branchIdsParam?: number[],
    ) => {
      setUserDetails(userDetails);
      setMajorsIds(majorsIds ?? []);
      setAbstractCoursesIds(abstractCoursesIds);
      setAbstractProgramIds(abstractProgramIds ?? []);
      setProgramStartYear(programStartYear);
      setBranchIds(branchIdsParam ?? []);

      // Reset to first page when filters change
      const newOffset = 0;
      setOffset(newOffset);
      await fetchUsers(newOffset, limit);
    };

    const handleUserSelect = (user: UserSummaryType) => {
      if (!loading && user) {
        setSelectedUser(user);
      }
    };

    const handleExportToExcel = () => {
      // Helper function to calculate age
      const calculateAge = (dateOfBirth: Date | string | undefined) => {
        if (!dateOfBirth) return '';

        const birthDate = new Date(dateOfBirth);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (
          monthDiff < 0 ||
          (monthDiff === 0 && today.getDate() < birthDate.getDate())
        ) {
          age--;
        }

        return age.toString();
      };

      // Helper function to format date
      const formatDate = (date: Date | string | undefined) => {
        if (!date) return '';
        return new Date(date).toLocaleDateString('he-IL');
      };

      // Create header row
      const headers = ['שם', 'ת.ז', 'טלפון נייד', 'גיל', 'תאריך לידה'];

      // Create data rows
      const dataRows = users.map((user) => [
        `${user.firstName} ${user.lastName}`,
        user.nationalId || '',
        user.phoneNumber || '',
        calculateAge(user.dateOfBirth),
        formatDate(user.dateOfBirth),
      ]);

      // Combine headers and data
      const excelData = [headers, ...dataRows];

      // Generate and download
      generateAndDownloadExcelFile(excelData);
    };

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '16px',
          paddingTop: '16px',
        }}
      >
        <div style={{ width: '300px' }}>
          <ColumnFilter
            addInstitution={false}
            maxHeight={'calc(100vh - 290px)'}
            updateSearchParameters={updateSearchParameters}
            setLoading={setLoading}
            addAbstractCourses={userRole === UserType.STUDENT}
            addAbstractProgram={userRole === UserType.STUDENT}
            addMajors={userRole === UserType.STUDENT}
            addProgramStartYear={userRole === UserType.STUDENT}
            addBranches={userRole === UserType.STUDENT}
            addUserDetails={true}
          />
        </div>
        <div style={{ width: '100%' }}>
          <DataTable
            emptyMessage={
              <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
            }
            selectionMode={'multiple'}
            multiple={true}
            selection={selectedUsers}
            groupRowsBy="dsf"
            onRowClick={(event: DataTableRowClickEvent) =>
              handleUserSelect(event.data as UserSummaryType)
            }
            value={users}
            loading={false}
            onSelectionChange={(e) => {
              const selected = e.value as UserSummaryType[];
              setSelectedUsers(selected);
              // Open dialog with the most recently selected user
              if (selected.length > 0) {
                // Get the newly selected user (the one that was just added)
                const newlySelected = selected.find(
                  (user) => !selectedUsers.some((su) => su.id === user.id),
                );
                if (newlySelected) {
                  setSelectedUser(newlySelected);
                } else if (selected.length === 1) {
                  // If only one user is selected, use that one
                  setSelectedUser(selected[0]);
                }
              } else {
                // If no users are selected, close the dialog
                setSelectedUser(undefined);
              }
            }}
            paginator
            rows={limit}
            first={offset}
            totalRecords={total}
            onPage={onPageChange}
            lazy={true}
            rowsPerPageOptions={[10, 20, 50, 100]}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            dataKey="id"
            scrollable
            scrollHeight="calc(100vh - 300px)"
            style={{ height: 'calc(100vh - 230px)' }}
            tableStyle={{
              direction: 'rtl',
            }}
          >
            <Column
              style={{ textAlign: 'right', width: '50px' }}
              field="lastName"
              header=""
              body={(rowData: any) => {
                return (
                  <UserAvatarSmall
                    imageUrl={rowData?.fileUrl}
                    firstName={rowData?.firstName}
                    lastName={rowData?.lastName}
                  />
                );
              }}
            />
            <Column
              style={{ textAlign: 'right', width: '150px' }}
              field="firstName"
              header="שם"
              body={(rowData: any) => {
                return (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <label style={{ fontWeight: 'bold' }}>
                      {rowData.lastName} {rowData.firstName}
                    </label>
                  </div>
                );
              }}
            />
            <Column
              field="nationalId"
              header="ת.ז"
              style={{ textAlign: 'right' }}
              body={(rowData: any) => {
                return <label>{rowData.nationalId}</label>;
              }}
            />
            <Column
              field="phoneNumber"
              header="טלפון נייד"
              style={{ textAlign: 'right' }}
              body={(rowData: any) => {
                return (
                  <PhoneNumberComponent phoneNumber={rowData.phoneNumber} />
                );
              }}
            />

            <Column
              field="email"
              header="דוא״ל"
              style={{ textAlign: 'right' }}
            />

            <Column
              field="address"
              header="כתובת"
              style={{ textAlign: 'right' }}
              body={(rowData: any) => {
                const parts = [
                  rowData.city,
                  rowData.street,
                  rowData.houseNumber,
                  rowData.flatNumber,
                ].filter(Boolean);

                return parts.length > 0 ? parts.join(', ') : '-';
              }}
            />

            <Column
              field="address"
              header="מגדר"
              style={{ textAlign: 'right' }}
              body={(rowData: any) => {
                return (
                  <div>{rowData.gender === Gender.Male ? 'זכר' : 'נקבה'}</div>
                );
              }}
            />

            <Column
              field="dateOfBirth1"
              header="גיל"
              style={{ textAlign: 'right' }}
              body={(rowData: any) => {
                if (!rowData.dateOfBirth) return '';

                const birthDate = new Date(rowData.dateOfBirth);
                const today = new Date();
                let age = today.getFullYear() - birthDate.getFullYear();
                const monthDiff = today.getMonth() - birthDate.getMonth();

                if (
                  monthDiff < 0 ||
                  (monthDiff === 0 && today.getDate() < birthDate.getDate())
                ) {
                  age--;
                }

                return age;
              }}
            />

            <Column
              field="dateOfBirth1"
              header="תאריך לידה"
              style={{ textAlign: 'right' }}
              body={(rowData: any) => {
                return (
                  <label>
                    {rowData.dateOfBirth
                      ? new Date(rowData.dateOfBirth).toLocaleDateString(
                          'he-IL',
                        ) // Format date to 'dd/mm/yyyy' for Hebrew locale
                      : ''}
                  </label>
                );
              }}
            />

            <Column
              field="dateOfBirth1"
              header={() => (
                <Button
                  style={{
                    height: '30px',
                    width: '30px',
                    backgroundColor: '#10b981',
                    borderColor: '#10b981',
                    marginLeft: '20px',
                  }}
                  icon="pi pi-file-excel"
                  tooltip="ייצוא לאקסל"
                  onClick={handleExportToExcel}
                />
              )}
              body={(rowData: UserSummaryType) => {
                const hasEmail = !!rowData.email && rowData.email.trim() !== '';
                return (
                  <Button
                    icon="pi pi-envelope"
                    style={{
                      height: '30px',
                      width: '30px',
                      backgroundColor: '#0078d4',
                      borderColor: '#0078d4',
                    }}
                    tooltip={
                      hasEmail
                        ? 'שליחת מייל באאוטלוק'
                        : 'אין כתובת מייל למשתמש זה'
                    }
                    tooltipOptions={{ position: 'top', showOnDisabled: true }}
                    disabled={!hasEmail}
                    onClick={(e) => {
                      e.stopPropagation();
                      openOutlookForUser(rowData);
                    }}
                    aria-label="Send mail via Outlook"
                  />
                );
              }}
              style={{ textAlign: 'right', width: '30px' }}
            />
          </DataTable>
        </div>

        <OutlookMailDialog
          visible={showOutlookDialog}
          onHide={() => setShowOutlookDialog(false)}
          users={outlookUsers}
          title={outlookTitle}
        />
      </div>
    );
  },
);
